import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse
} from '@angular/common/http';
import { Router } from '@angular/router';
import { NgZone } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HotToastService } from '@ngxpert/hot-toast';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { ActivatedRouteSnapshot } from '@angular/router';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';

declare const globalThis: any;

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private _unauthorizedDialogOpen = false;
  private _isRedirectingToSignIn = false;
  private _unauthorizedRedirectTimer: ReturnType<typeof setTimeout> | null = null;
  private _unauthorizedDialogRef: MatDialogRef<any> | null = null;

  constructor(
    private readonly _toastService: HotToastService,
    private readonly _fuseConfirmationService: FuseConfirmationService,
    private readonly _router: Router,
    private readonly _ngZone: NgZone
  ) { }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {

        // Utilisateur non autorisé (erreur métier venant du back)
        if (
          error.error?.item3 === 'Utilisateur non autorisé.' ||
          error.error?.message?.includes('Utilisateur non autorisé')
        ) {
          this._ngZone.run(() => {
            this._router.navigateByUrl('/forbidden-access');
          });
          return throwError(() => error);
        }

        // Erreurs de validation (ancien format)
        if (error.status === 422 && error.error?.errors) {
          const validationErrors = error.error.errors;
          const combinedErrors = validationErrors.join('<br/> - ');

          this._toastService.error(
            `Les erreurs suivantes se sont produites :<br/><br/> - ${combinedErrors}`,
            { duration: 4000 }
          );

          return throwError(() => error);
        }

        // 🔹 Nouvelle gestion des erreurs métier simples (400 avec message)
        if (error.status === 400) {
          const message =
            typeof error.error === 'string'
              ? error.error
              : error.error?.message || 'Une erreur fonctionnelle est survenue';

          this._toastService.error(message);
          return throwError(() => error);
        }

        // Gestion authentification
        if (error.status === 401) {
          // Ne pas traiter comme "session expirée" pour les endpoints d'auth
          // et pour les routes publiques (contexte NoAuthGuard)
          if (this.isAuthApiRequest(req.url) || this.isNoAuthRouteContext()) {
            return throwError(() => error);
          }

          // N'afficher la popup que si un token local existe ET est réellement expiré
          if (!this.hasExpiredAccessToken()) {
            return throwError(() => error);
          }

          this.handleUnauthorized();
          return throwError(() => error);
        }

        // Ignorer refresh token + conflits
        if ((error.status === 500 && req.url.includes('/refresh-token')) || error.status === 409) {
          return throwError(() => error);
        }

        // Erreur serveur générique
        this._toastService.error('Une erreur serveur est survenue.');

        return throwError(() => error);
      })
    );
  }

  private isAuthApiRequest(requestUrl: string): boolean {
    return requestUrl.includes('/api/auth/');
  }

  private isNoAuthRouteContext(): boolean {
    return this.routeTreeContainsNoAuthGuard(this._router.routerState.snapshot.root);
  }

  private routeTreeContainsNoAuthGuard(snapshot: ActivatedRouteSnapshot | null): boolean {
    if (!snapshot) {
      return false;
    }

    const routeConfig = snapshot.routeConfig;
    if (routeConfig) {
      const guards = [
        ...(routeConfig.canActivate ?? []),
        ...(routeConfig.canActivateChild ?? []),
        ...(routeConfig.canLoad ?? [])
      ];

      if (guards.includes(NoAuthGuard)) {
        return true;
      }
    }

    for (const child of snapshot.children ?? []) {
      if (this.routeTreeContainsNoAuthGuard(child)) {
        return true;
      }
    }

    return false;
  }

  private hasExpiredAccessToken(): boolean {
    const token = globalThis?.localStorage?.getItem?.('accessToken') ?? '';
    if (!token.trim()) {
      return false;
    }

    try {
      return AuthUtils.isTokenExpired(token);
    } catch {
      // Token malformé: on traite comme session invalide/expirée
      return true;
    }
  }

  private handleUnauthorized(): void {
    if (this._isRedirectingToSignIn) {
      return;
    }

    // Anti-spam: un seul modal à la fois.
    if (this._unauthorizedDialogOpen) {
      // Si le modal est déjà affiché, on s'assure juste que la redirection auto est programmée.
      if (!this._unauthorizedRedirectTimer) {
        this._unauthorizedRedirectTimer = setTimeout(() => this.redirectToSignIn(), 3000);
      }
      return;
    }

    this._unauthorizedDialogOpen = true;

    const dialogRef = this._fuseConfirmationService.open({
      title: 'Session expirée',
      message:
        "Vous n'êtes plus connecté, vous allez être redirigé vers l'écran d'authentification.",
      icon: {
        show: true,
        name: 'heroicons_outline:exclamation',
        color: 'warning'
      },
      actions: {
        confirm: { color: 'primary', label: 'Se connecter' },
        cancel: { show: false }
      },
      dismissible: false
    });

    this._unauthorizedDialogRef = dialogRef;

    this._unauthorizedRedirectTimer = setTimeout(() => this.redirectToSignIn(), 3000);

    dialogRef.afterClosed().subscribe(result => {
      if (this._unauthorizedRedirectTimer) {
        clearTimeout(this._unauthorizedRedirectTimer);
        this._unauthorizedRedirectTimer = null;
      }

      this._unauthorizedDialogOpen = false;
      this._unauthorizedDialogRef = null;

      if (result === 'confirmed') {
        this.redirectToSignIn();
      }
    });
  }

  private redirectToSignIn(): void {
    if (this._isRedirectingToSignIn) {
      return;
    }
    this._isRedirectingToSignIn = true;

    // Le modal est non-dismissible (bloquant) : on le ferme explicitement avant de naviguer.
    this._unauthorizedDialogRef?.close();
    this._unauthorizedDialogRef = null;

    this._ngZone.run(() => {
      this._router.navigateByUrl('/sign-in');
    });
  }
}
